import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Lightbox } from '../../../ui';
import {
  productImages,
  sizedImageUrl,
} from '../../../components/productCard';

/**
 * Main 4:3 image with thumbnail buttons. Left/Right arrow keys move between images
 * (focus follows when it's on a thumbnail); the main image opens a full-size preview.
 * Mount with key={product.id} so the selection resets for each product.
 */
const ProductGallery = ({ product }) => {
  const images = productImages(product);
  const count = images.length;
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState(() => new Set());
  const [previewIndex, setPreviewIndex] = useState(null);
  const thumbRefs = useRef(new Map());

  const name = product?.name || 'Project';
  const letter = name.trim().charAt(0).toUpperCase() || '?';
  const markFailed = url => setFailed(prev => new Set(prev).add(url));

  if (count === 0) {
    return (
      <div className="pd-gallery">
        <div className="pd-gallery__frame">
          <span className="pd-gallery__empty" aria-hidden="true">
            {letter}
          </span>
        </div>
      </div>
    );
  }

  const current = Math.min(index, count - 1);
  const [image] = images.slice(current, current + 1);

  const go = (next, moveFocus) => {
    const wrapped = (next + count) % count;
    setIndex(wrapped);
    if (moveFocus) thumbRefs.current.get(wrapped)?.focus();
  };

  const handleKeyDown = event => {
    if (count < 2) return;
    const onThumb = [...thumbRefs.current.values()].includes(document.activeElement);
    let next = null;
    if (event.key === 'ArrowRight') next = current + 1;
    else if (event.key === 'ArrowLeft') next = current - 1;
    else if (event.key === 'Home' && onThumb) next = 0;
    else if (event.key === 'End' && onThumb) next = count - 1;
    if (next === null) return;
    event.preventDefault();
    go(next, onThumb);
  };

  return (
    <>
      <div
        className="pd-gallery"
        role="group"
        aria-label={`${name} images`}
        onKeyDown={handleKeyDown}
      >
        <div className="pd-gallery__frame">
          {failed.has(image.url) ? (
            <span className="pd-gallery__empty" aria-hidden="true">
              {letter}
            </span>
          ) : (
            <button
              type="button"
              className="pd-gallery__open"
              onClick={() => setPreviewIndex(current)}
              aria-label={`Enlarge image ${current + 1} of ${count}`}
            >
              <img
                className="pd-gallery__img"
                src={sizedImageUrl(image.url, 1200)}
                alt=""
                decoding="async"
                onError={() => markFailed(image.url)}
              />
            </button>
          )}

          {count > 1 && (
            <>
              <button
                type="button"
                className="pd-gallery__nav pd-gallery__nav--prev"
                onClick={() => go(current - 1, false)}
                aria-label="Previous image"
              >
                <ChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                className="pd-gallery__nav pd-gallery__nav--next"
                onClick={() => go(current + 1, false)}
                aria-label="Next image"
              >
                <ChevronRight aria-hidden="true" />
              </button>
              <span className="pd-gallery__count" aria-live="polite">
                {current + 1} / {count}
              </span>
            </>
          )}
        </div>

        {count > 1 && (
          <div className="pd-gallery__thumbs" role="group" aria-label="Choose image">
            {images.map((item, i) => (
              <button
                key={`${item.url}-${i}`}
                ref={el => {
                  if (el) thumbRefs.current.set(i, el);
                  else thumbRefs.current.delete(i);
                }}
                type="button"
                className="pd-gallery__thumb"
                aria-label={`Show image ${i + 1} of ${count}`}
                aria-pressed={i === current}
                onClick={() => setIndex(i)}
              >
                {failed.has(item.url) ? (
                  <span className="pd-gallery__thumb-empty" aria-hidden="true">
                    {letter}
                  </span>
                ) : (
                  <img
                    src={sizedImageUrl(item.url, 240)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => markFailed(item.url)}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Rendered outside the group so its own arrow keys don't also move the gallery. */}
      <Lightbox
        images={images.map((item, i) => ({
          src: sizedImageUrl(item.url, 1600),
          alt: item.title ? `${name}: ${item.title}` : `${name}, image ${i + 1}`,
        }))}
        index={previewIndex}
        onIndexChange={i => {
          setPreviewIndex(i);
          setIndex(i);
        }}
        onClose={() => setPreviewIndex(null)}
      />
    </>
  );
};

export default ProductGallery;
